import { DigitalPassport, GameCard } from "../types/battle"
import { normalizeGameCard } from "./gameCards"

export type PassportAuditSeverity = "error" | "warn" | "info"

export interface PassportAuditIssue {
  severity: PassportAuditSeverity
  field: string
  message: string
  raw?: unknown
}

type RawRecord = Record<string, unknown>

let lastRawPassportResponse: unknown = null

export function setLastRawPassportResponse(raw: unknown): void {
  lastRawPassportResponse = raw
}

export function getLastRawPassportResponse(): unknown {
  return lastRawPassportResponse
}

function unwrapSource(raw: unknown): RawRecord | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const nested = obj.data ?? obj.result ?? obj.payload ?? obj.passport
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...(nested as RawRecord), ...obj }
  }
  return obj
}

function hasOwn(obj: RawRecord, ...keys: string[]): boolean {
  return keys.some((key) => obj[key] !== undefined && obj[key] !== null)
}

export function isRenderableGameCard(
  card: GameCard | null | undefined
): card is GameCard {
  if (!card?.id?.trim()) return false
  if (!Number.isFinite(card.level) || card.level < 1) return false
  if (!Number.isFinite(card.basePower)) return false
  return true
}

export function auditPassportRaw(
  raw: unknown,
  normalized: DigitalPassport | null
): PassportAuditIssue[] {
  const issues: PassportAuditIssue[] = []
  const source = unwrapSource(raw)

  if (!source) {
    issues.push({
      severity: "error",
      field: "payload",
      message: "Ответ API не объект",
      raw,
    })
    return issues
  }

  if (!normalized) {
    issues.push({
      severity: "error",
      field: "passport",
      message: "normalizeDigitalPassport вернул null",
      raw: source,
    })
    return issues
  }

  const nicknameRaw = source.nickname ?? source.name
  if (nicknameRaw === "" || nicknameRaw === null) {
    issues.push({
      severity: "warn",
      field: "nickname",
      message: "Пустой nickname в сыром ответе (legacy)",
      raw: nicknameRaw,
    })
  }

  if (!hasOwn(source, "publicGameCode", "public_game_code", "gameCode", "game_code")) {
    issues.push({
      severity: "warn",
      field: "publicGameCode",
      message: "Нет publicGameCode в сыром ответе",
    })
  }

  if (!hasOwn(source, "maxEnergy", "max_energy")) {
    issues.push({
      severity: "info",
      field: "maxEnergy",
      message: "maxEnergy отсутствует — подставлен дефолт 100",
    })
  }

  if (!hasOwn(source, "qrBattlesToday", "qr_battles_today")) {
    issues.push({
      severity: "info",
      field: "qrBattlesToday",
      message: "qrBattlesToday отсутствует — подставлен 0",
    })
  }

  if (!hasOwn(source, "chakraCharges", "chakra_charges", "charges", "chakra")) {
    issues.push({
      severity: "info",
      field: "chakraCharges",
      message: "chakraCharges отсутствует — подставлен 0",
    })
  }

  const leagueRaw = source.academicLeague ?? source.academic_league ?? source.league
  if (leagueRaw != null && leagueRaw !== "") {
    const league = String(leagueRaw).toUpperCase()
    if (league !== "TITAN" && league !== "ATLANT") {
      issues.push({
        severity: "warn",
        field: "academicLeague",
        message: `Нестандартная лига: ${String(leagueRaw)}`,
        raw: leagueRaw,
      })
    }
  } else {
    issues.push({
      severity: "info",
      field: "academicLeague",
      message: "Лига не выбрана (старый профиль до Академии?)",
    })
  }

  const activeCardRaw = source.activeCard ?? source.active_card
  const activeCardIdRaw =
    source.activeCardId ?? source.active_card_id ?? source.activeCard_id

  if (activeCardIdRaw && !activeCardRaw) {
    issues.push({
      severity: "warn",
      field: "activeCard",
      message: `activeCardId=${String(activeCardIdRaw)}, но activeCard не вложен в ответ`,
      raw: activeCardIdRaw,
    })
  }

  if (activeCardRaw != null) {
    if (typeof activeCardRaw !== "object" || Array.isArray(activeCardRaw)) {
      issues.push({
        severity: "error",
        field: "activeCard",
        message: "activeCard не объект",
        raw: activeCardRaw,
      })
    } else {
      const cardObj = activeCardRaw as RawRecord
      const nested = cardObj.card ?? cardObj.gameCard ?? cardObj.game_card
      if (
        nested &&
        !hasOwn(cardObj, "id", "uuid", "cardId", "card_id")
      ) {
        issues.push({
          severity: "warn",
          field: "activeCard",
          message: "activeCard вложен (legacy: activeCard.card)",
          raw: cardObj,
        })
      }

      const cardId =
        cardObj.id ?? cardObj.uuid ?? cardObj.cardId ?? cardObj.card_id
      if (cardId == null || String(cardId).trim() === "") {
        issues.push({
          severity: "error",
          field: "activeCard.id",
          message: "activeCard без id",
          raw: cardObj,
        })
      }

      if (cardObj.element == null) {
        issues.push({
          severity: "warn",
          field: "activeCard.element",
          message: "activeCard без element — подставлен FIRE",
          raw: cardObj,
        })
      }

      const levelRaw = cardObj.level
      if (levelRaw != null && Number(levelRaw) < 1) {
        issues.push({
          severity: "warn",
          field: "activeCard.level",
          message: `level=${String(levelRaw)} — поднят до 1`,
          raw: levelRaw,
        })
      }

      const rankRaw =
        cardObj.colorRank ?? cardObj.color_rank ?? cardObj.rank
      if (rankRaw != null) {
        const rank = String(rankRaw).toUpperCase()
        const allowed = ["WHITE", "GREEN", "BLUE", "PURPLE", "ORANGE", "RED"]
        if (!allowed.includes(rank)) {
          issues.push({
            severity: "warn",
            field: "activeCard.colorRank",
            message: `Нестандартный ранг: ${String(rankRaw)} → WHITE`,
            raw: rankRaw,
          })
        }
      }
    }
  }

  if (normalized.activeCardId && !normalized.activeCard) {
    issues.push({
      severity: "warn",
      field: "activeCard",
      message: `После нормализации activeCardId=${normalized.activeCardId}, но activeCard=null`,
    })
  }

  if (normalized.activeCard && !isRenderableGameCard(normalized.activeCard)) {
    issues.push({
      severity: "error",
      field: "activeCard",
      message: "activeCard после нормализации не пригоден для рендера",
      raw: normalized.activeCard,
    })
  }

  if (
    normalized.activeCardId &&
    normalized.activeCard &&
    normalized.activeCardId !== normalized.activeCard.id
  ) {
    issues.push({
      severity: "warn",
      field: "activeCardId",
      message: `activeCardId (${normalized.activeCardId}) ≠ activeCard.id (${normalized.activeCard.id})`,
    })
  }

  if (normalized.maxEnergy < 1) {
    issues.push({
      severity: "warn",
      field: "maxEnergy",
      message: `maxEnergy=${normalized.maxEnergy} — исправлен на 100`,
    })
  }

  return issues
}

export function logPassportAudit(
  raw: unknown,
  normalized: DigitalPassport | null
): PassportAuditIssue[] {
  const issues = auditPassportRaw(raw, normalized)
  const errors = issues.filter((i) => i.severity === "error")
  const warns = issues.filter((i) => i.severity === "warn")

  console.log("[PASSPORT] normalized summary:", {
    nickname: normalized?.nickname,
    code: normalized?.publicGameCode,
    league: normalized?.academicLeague,
    activeCardId: normalized?.activeCardId,
    activeCard: normalized?.activeCard
      ? {
          id: normalized.activeCard.id,
          element: normalized.activeCard.element,
          level: normalized.activeCard.level,
          colorRank: normalized.activeCard.colorRank,
        }
      : null,
    energy: normalized ? `${normalized.energy}/${normalized.maxEnergy}` : null,
  })

  if (issues.length === 0) {
    console.log("[PASSPORT] audit: ok, legacy-проблем не найдено")
    return issues
  }

  console.log(
    `[PASSPORT] audit: ${errors.length} error(s), ${warns.length} warn(s), ${issues.length - errors.length - warns.length} info`
  )
  for (const issue of issues) {
    const tag =
      issue.severity === "error"
        ? "ERROR"
        : issue.severity === "warn"
          ? "WARN"
          : "INFO"
    console.log(`[PASSPORT] ${tag} ${issue.field}: ${issue.message}`, issue.raw ?? "")
  }

  return issues
}

/** Повторная нормализация карты перед рендером */
export function sanitizeActiveCard(
  card: GameCard | null | undefined
): GameCard | null {
  if (!card) return null
  return normalizeGameCard(card) ?? null
}
